import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, ListItemText, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface RowAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  ariaLabel?: string;
}

/** Collapses a row's action buttons into a single icon-triggered dropdown menu,
 * used across the Catalogue Governance tabs where rows have 3-5 possible actions. */
const RowActionsMenu = ({ actions, ariaLabel }: RowActionsMenuProps) => {
  const { t } = useTranslation('catalogueGovernance');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (action: RowAction) => {
    handleClose();
    action.onClick();
  };

  return (
    <>
      <IconButton size="small"
        aria-label={ariaLabel ?? t('rowActionsMenu.ariaLabel', 'Row actions')}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}>
        {actions.map((action) => (
          <MenuItem key={action.label}
            disabled={action.disabled}
            onClick={() => handleSelect(action)}
            sx={action.destructive ? { color: 'error.main' } : undefined}>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RowActionsMenu;
